import { useEffect, useCallback } from 'react';

// 접근성 향상을 위한 훅들

// 키보드 네비게이션 훅
export const useKeyboardNavigation = (
  elements: HTMLElement[],
  onEnter?: (element: HTMLElement, index: number) => void
) => {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const currentIndex = elements.findIndex(el => el === document.activeElement);
      
      switch (e.key) {
        case 'ArrowDown':
        case 'ArrowRight':
          e.preventDefault();
          const nextIndex = (currentIndex + 1) % elements.length;
          elements[nextIndex]?.focus();
          break;
          
        case 'ArrowUp':
        case 'ArrowLeft':
          e.preventDefault();
          const prevIndex = (currentIndex - 1 + elements.length) % elements.length;
          elements[prevIndex]?.focus();
          break;
          
        case 'Enter':
        case ' ':
          e.preventDefault();
          const focusedElement = document.activeElement as HTMLElement;
          const elementIndex = elements.indexOf(focusedElement);
          
          if (elementIndex >= 0) {
            onEnter?.(focusedElement, elementIndex);
            focusedElement.click();
          }
          break;
          
        case 'Escape':
          e.preventDefault();
          (document.activeElement as HTMLElement)?.blur();
          break;
      }
    };

    elements.forEach(el => {
      if (el) {
        el.tabIndex = el.tabIndex >= 0 ? el.tabIndex : 0;
        el.addEventListener('keydown', handleKeyDown);
      }
    });

    return () => {
      elements.forEach(el => {
        el?.removeEventListener('keydown', handleKeyDown);
      });
    };
  }, [elements, onEnter]);
};

// 포커스 트랩 훅 (모달, 패널 등에서 사용)
export const useFocusTrap = (containerRef: React.RefObject<HTMLElement>, isActive: boolean) => {
  useEffect(() => {
    if (!isActive || !containerRef.current) return;

    const container = containerRef.current;
    const focusableElements = container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    container.addEventListener('keydown', handleTabKey);
    firstElement?.focus();

    return () => {
      container.removeEventListener('keydown', handleTabKey);
    };
  }, [containerRef, isActive]);
};

// 스크린 리더 알림 훅
export const useScreenReaderAnnouncement = () => {
  const announce = useCallback((message: string, priority: 'polite' | 'assertive' = 'polite') => {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', priority);
    announcement.setAttribute('aria-atomic', 'true');
    announcement.setAttribute('class', 'sr-only');
    announcement.textContent = message;
    
    document.body.appendChild(announcement);
    
    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  }, []);

  return announce;
};

// 텍스트 읽기 가능성 향상 (대비 검사 등)
export const useTextReadability = () => {
  const checkContrast = useCallback((backgroundColor: string, textColor: string): number => {
    // 간단한 대비율 계산
    const rgb1 = hexToRgb(backgroundColor);
    const rgb2 = hexToRgb(textColor);
    
    if (!rgb1 || !rgb2) return 0;
    
    const luminance1 = calculateLuminance(rgb1);
    const luminance2 = calculateLuminance(rgb2);
    
    const lighter = Math.max(luminance1, luminance2);
    const darker = Math.min(luminance1, luminance2);
    
    return (lighter + 0.05) / (darker + 0.05);
  }, []);

  const suggestTextColor = useCallback((backgroundColor: string): string => {
    const rgb = hexToRgb(backgroundColor);
    if (!rgb) return '#000000';
    
    const luminance = calculateLuminance(rgb);
    return luminance > 0.5 ? '#000000' : '#ffffff';
  }, []);

  return { checkContrast, suggestTextColor };
};

// 헬퍼 함수들
const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

const calculateLuminance = (rgb: { r: number; g: number; b: number }): number => {
  const { r, g, b } = rgb;
  const [rs, gs, bs] = [r, g, b].map(c => {
    c = c / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

// 고대비 모드 감지
export const useHighContrastMode = () => {
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-contrast: high)');
    
    const handleChange = (e: MediaQueryListEvent) => {
      document.documentElement.classList.toggle('high-contrast', e.matches);
    };
    
    mediaQuery.addEventListener('change', handleChange);
    handleChange(mediaQuery as any);
    
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);
};

// 모션 감소 모드 감지
export const useReducedMotion = () => {
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    
    const handleChange = (e: MediaQueryListEvent) => {
      document.documentElement.classList.toggle('reduce-motion', e.matches);
    };
    
    mediaQuery.addEventListener('change', handleChange);
    handleChange(mediaQuery as any);
    
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);
};